import { useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../../convex/_generated/api'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Bell } from 'lucide-react'
import { cn, formatDateTime } from '@/lib/utils'
import { useChatPanel } from '@/components/chat/ChatContext'

export function NotificationBell() {
  const { isAuthenticated, profile } = useAuth()
  const navigate = useNavigate()
  const { openChat } = useChatPanel()
  const [open, setOpen] = useState(false)

  const unread = useQuery(
    api.notifications.getUnreadCount,
    isAuthenticated && profile ? {} : 'skip'
  )
  const list = useQuery(
    api.notifications.getNotifications,
    isAuthenticated && profile && open ? { limit: 30 } : 'skip'
  )
  const markRead = useMutation(api.notifications.markNotificationAsRead)
  const markAll = useMutation(api.notifications.markAllNotificationsAsRead)

  if (!isAuthenticated || !profile?.active) return null

  const count = unread ?? 0

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="relative"
        aria-label="Notifiche"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            aria-hidden
            onClick={() => setOpen(false)}
          />
          <div
            className={cn(
              'absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-1.5rem))] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl'
            )}
          >
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-sm font-semibold text-gray-900">Notifiche</span>
              {count > 0 && (
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => void markAll({})}
                >
                  Segna tutte lette
                </button>
              )}
            </div>
            <ul className="max-h-80 overflow-y-auto">
              {list === undefined && (
                <li className="px-3 py-4 text-sm text-gray-500">Caricamento…</li>
              )}
              {list && list.length === 0 && (
                <li className="px-3 py-4 text-sm text-gray-500">Nessuna notifica</li>
              )}
              {list?.map((n) => (
                <li key={n._id}>
                  <button
                    type="button"
                    className={cn(
                      'w-full px-3 py-2.5 text-left hover:bg-gray-50',
                      !n.letto && 'bg-primary/5'
                    )}
                    onClick={async () => {
                      if (!n.letto) await markRead({ notificationId: n._id })
                      setOpen(false)
                      if (n.prescription_id && n.chat_message_id) {
                        openChat(n.prescription_id)
                      } else if (n.prescription_id) {
                        navigate(`/prescriptions/${n.prescription_id}`)
                      }
                    }}
                  >
                    <div className="text-sm font-medium text-gray-900">{n.titolo}</div>
                    <div className="mt-0.5 text-xs text-gray-600 line-clamp-2">{n.messaggio}</div>
                    <div className="mt-1 text-[10px] text-gray-400">
                      {formatDateTime(n.data || n.created_at)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}
